import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  directory,
  documentation,
  isForbidden,
  notifications,
  schedule,
  timekeeping,
  timesheets,
} from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, useTabListKeys } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import {
  WORKER_TABS,
  clockState,
  clockTime,
  dayLabel,
  hours,
  notesOwed,
  shiftLine,
  shiftsOn,
  todaySummary,
  type WorkerTab,
} from './workerWebLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * The worker experience, on the web.
 *
 * ---------------------------------------------------------------------------
 * What this is, and what it deliberately is not
 * ---------------------------------------------------------------------------
 *
 * It is the SAME domain contracts the worker mobile app reads — `/auth/me`,
 * `/directory/workers/me`, `/timekeeping/workers/:id/current`,
 * `/schedule/workers/:id/week`, `/documentation/to-write`, `/notifications`,
 * `/timesheets/workers/:id/week`. No business rule is re-implemented here: if
 * this screen and the phone ever disagreed about whether somebody is clocked
 * in, one of them would be lying, and the only way to prevent that is for
 * neither of them to decide.
 *
 * It is NOT impersonation. Every request is made as the signed-in principal
 * and scoped by the server to their own worker record — `/directory/workers/me`
 * is authenticated-only and returns the CALLER's row, so there is no parameter
 * on this screen that could name somebody else. An administrator opening it
 * sees their own worker record, which is the honest meaning of "preview": you
 * are looking at the shape of the experience through your own account.
 *
 * Previewing ANOTHER worker's day would need a backend contract that does not
 * exist — a scoped, audited, explicitly-consented view — and inventing one on
 * the client by passing a different worker id would be exactly the permission
 * bypass this screen promises it is not. So it is not offered.
 *
 * Responsive by construction: one column on a phone, two where there is room.
 */

export function WorkerWeb() {
  const { me } = useAuth();
  const [tab, setTab] = useState<WorkerTab>('today');
  const workerTabs = useTabListKeys<HTMLElement>();
  const today = useMemo(() => new Date(), []);

  // The caller's OWN worker row. Authenticated-only, and the server chooses
  // which row that is — this screen never names a worker.
  const worker = useQuery({
    queryKey: ['worker-web', 'me'],
    queryFn: () => directory.me(),
    retry: false,
  });
  const workerId = worker.data?.id ?? null;

  const current = useQuery({
    queryKey: ['worker-web', 'current', workerId],
    queryFn: () => timekeeping.currentSession(workerId!),
    enabled: workerId != null,
    refetchInterval: 60_000,
  });
  const week = useQuery({
    queryKey: ['worker-web', 'week', workerId],
    queryFn: () => schedule.workerWeek(workerId!),
    enabled: workerId != null,
  });
  const sheet = useQuery({
    queryKey: ['worker-web', 'timesheet', workerId],
    queryFn: () => timesheets.workerWeek(workerId!),
    enabled: workerId != null,
    retry: false,
  });
  const notes = useQuery({
    queryKey: ['worker-web', 'to-write'],
    queryFn: () => documentation.toWrite(),
    retry: false,
  });
  const unread = useQuery({
    queryKey: ['worker-web', 'unread'],
    queryFn: () => notifications.unreadCount(),
    retry: false,
  });
  const sessions = useQuery({
    queryKey: ['worker-web', 'sessions', workerId],
    // The last fortnight, which is what "recent" means to somebody checking
    // their own clock history. The client has no limit parameter; the window is
    // the filter.
    queryFn: () =>
      timekeeping.workerSessions(workerId!, {
        since: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      }),
    enabled: workerId != null,
  });

  if (worker.isLoading) return <SkeletonSet surface="table" rows={5} />;
  if (worker.isError || !worker.data) {
    // A principal with no worker record is a real, ordinary case — an owner or
    // a billing contact who is not staff. It is not an error to retry.
    return (
      <div className="rn-card" role="status">
        <h2 className="rn-card-title">No worker record for this account</h2>
        <p className="rn-muted">
          The worker experience shows one person&rsquo;s own shifts, time and notes, so it needs a
          worker record. {me?.name ?? 'This account'} is a member of the organization but is not set
          up as staff.
        </p>
      </div>
    );
  }

  const w = worker.data;
  const clock = clockState(current.data);
  const shiftsToday = shiftsOn(week.data, today);
  const owed = notesOwed(notes.data);

  return (
    <div className="ww">
      <header className="ww-head">
        <div>
          <h1 className="rn-card-title" style={{ margin: 0 }}>
            {w.displayName}
          </h1>
          <p className="rn-muted" style={{ margin: '4px 0 0' }}>
            {w.roleTitle ?? 'Worker'}
          </p>
        </div>
        <Pill variant={clock.clockedIn ? 'good' : 'plain'} dot>
          {clock.headline}
        </Pill>
      </header>

      {/* `aria-pressed` removed (invalid on `role="tab"`); ids + aria-controls
          added so the strip actually owns the section below it. */}
      <nav className="rn-tabs ww-tabs" role="tablist" aria-label="Worker sections" {...workerTabs}>
        {WORKER_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`ww-tab-${t.key}`}
            aria-controls="ww-panel"
            className="rn-tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div id="ww-panel" role="tabpanel" aria-labelledby={`ww-tab-${tab}`}>
        {tab === 'today' ? (
          <div className="ww-col">
            <section className="rn-card">
              <h2 className="rn-card-title">
                {todaySummary({
                  // Null, not a length over `?? []`: the headline must not say
                  // "Nothing scheduled today" because the schedule read failed.
                  shiftsToday: week.data === undefined ? null : shiftsToday.length,
                  notesOwed: notes.data === undefined ? null : owed.length,
                  unreadNotifications: unread.data ?? null,
                })}
              </h2>
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                {clock.detail}
              </p>
            </section>

            <section className="rn-card">
              <h2 className="rn-card-title">Today&rsquo;s shifts</h2>
              {week.isLoading ? (
                <SkeletonSet surface="table" rows={2} />
              ) : week.isError && week.data === undefined ? (
                // "Nothing scheduled today" is the sentence that decides whether
                // somebody goes to work. It was being rendered from a failure.
                <LoadFailure
                  error={week.error}
                  message="We couldn’t load your schedule just now. Do not read this as an empty day — check with your supervisor if you are unsure."
                  onRetry={() => void week.refetch()}
                />
              ) : shiftsToday.length === 0 ? (
                <SuccessEmpty
                  heading="Nothing scheduled today"
                  message="Your next shifts are under Schedule."
                />
              ) : (
                <ul className="tc-rows">
                  {shiftsToday.map((s) => (
                    <li key={s.id} className="tc-row">
                      <div className="tc-day">
                        <span className="tc-row-name">{shiftLine(s)}</span>
                        {s.notes ? <p className="rn-muted">{s.notes}</p> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {owed.length > 0 ? (
              <section className="rn-card">
                <div className="rn-card-head">
                  <h2 className="rn-card-title">Notes to write</h2>
                  <button type="button" className="rn-btn-quiet" onClick={() => setTab('notes')}>
                    See all {owed.length}
                  </button>
                </div>
                <ul className="tc-rows">
                  {owed.slice(0, 3).map((n) => (
                    <li key={n.serviceEventId} className="tc-row">
                      <div className="tc-day">
                        <span className="tc-row-name">
                          {n.clientInitials} · {dayLabel(n.occurredAt)}
                        </span>
                        <p className="rn-muted">{noteState(n)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}

        {tab === 'schedule' ? (
          <section className="rn-card">
            <div className="rn-card-head">
              <h2 className="rn-card-title">This week</h2>
              {week.data ? (
                <span className="rn-muted">
                  {week.data.summary.total} shift{week.data.summary.total === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            {/* `week.isError` alone destroyed rows that HAD loaded: react-query
                keeps `data` across a failed refresh, so a dropped connection
                blanked a schedule the worker was reading. `listPhase` keeps the
                last good answer, including its emptiness. */}
            <ListState
              phase={listPhase(week, week.data === undefined ? undefined : week.data.shifts.length)}
              surface="table"
              rows={5}
              failedMessage="We couldn’t load your schedule just now. Do not read this as an empty week."
              onRetry={() => void week.refetch()}
              empty={<SuccessEmpty heading="Nothing scheduled" message="No shifts this week." />}
            >
              <ul className="tc-rows">
                {[...(week.data?.shifts ?? [])]
                  .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
                  .map((s) => (
                    <li key={s.id} className="tc-row">
                      <div className="tc-day">
                        <div className="tc-day-head">
                          <span className="tc-row-name">{dayLabel(s.startsAt)}</span>
                          <span className="rn-muted">{shiftLine(s)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </ListState>
          </section>
        ) : null}

        {tab === 'time' ? (
          <div className="ww-col">
            <section className="rn-card">
              <h2 className="rn-card-title">This week</h2>
              <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
                <div className="db-kpi">
                  <span className="db-kpi-label">Recorded</span>
                  <span className="db-kpi-value">
                    {sheet.isError
                      ? '—'
                      : sheet.isLoading
                        ? '…'
                        : sheet.data?.hasTimeClock === false
                          ? 'Not measured'
                          : hours(sheet.data?.aggregate.totalMinutes ?? null)}
                  </span>
                  <span className="db-kpi-foot">
                    <span className="rn-muted">
                      {/* CORE-AUD-216: no time clock is not a 0.00h week. */}
                      {sheet.data?.hasTimeClock === false
                        ? 'No time clock connected'
                        : w.expectedWeeklyHours != null
                          ? `Expected ${w.expectedWeeklyHours}h`
                          : 'No expected hours set'}
                    </span>
                  </span>
                </div>
                <div className="db-kpi">
                  <span className="db-kpi-label">Right now</span>
                  <span className="db-kpi-value" style={{ fontSize: 22 }}>
                    {clock.headline}
                  </span>
                  <span className="db-kpi-foot">
                    <span className="rn-muted">{clock.detail}</span>
                  </span>
                </div>
              </div>
              {isForbidden(sheet.error) ? (
                <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
                  Your timesheet is not visible to this account.
                </p>
              ) : null}
            </section>

            <section className="rn-card">
              <h2 className="rn-card-title">Recent sessions</h2>
              {sessions.isLoading ? (
                <SkeletonSet surface="table" rows={4} />
              ) : sessions.isError && sessions.data === undefined ? (
                <LoadFailure
                  error={sessions.error}
                  message="We couldn’t load your clock history just now."
                  onRetry={() => void sessions.refetch()}
                />
              ) : (sessions.data ?? []).length === 0 ? (
                <SuccessEmpty
                  heading="No sessions yet"
                  message="Your clock history appears here."
                />
              ) : (
                <ul className="tc-rows">
                  {[...(sessions.data ?? [])]
                    .sort((a, b) => b.clockedInAt.localeCompare(a.clockedInAt))
                    .slice(0, 10)
                    .map((s) => (
                      <li key={s.id} className="tc-row">
                        <div className="tc-day">
                          <div className="tc-day-head">
                            <span className="tc-row-name">{dayLabel(s.clockedInAt)}</span>
                            <span className="tc-elapsed">{hours(s.computedMinutes)}</span>
                          </div>
                          <p className="rn-muted">
                            {clockTime(s.clockedInAt)} –{' '}
                            {s.clockedOutAt ? clockTime(s.clockedOutAt) : 'still open'}
                            {/* Stated, never hidden: a session the system closed is
                            not the same evidence as one the worker closed. */}
                            {s.autoClosed ? ' · closed automatically' : ''}
                          </p>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}

        {tab === 'notes' ? (
          <section className="rn-card">
            <h2 className="rn-card-title">Notes to write</h2>
            {notes.isLoading ? (
              <SkeletonSet surface="table" rows={5} />
            ) : isForbidden(notes.error) ? (
              <p className="rn-muted">Documentation is not visible to this account.</p>
            ) : notes.isError && notes.data === undefined ? (
              // The 403 branch above was already right; every OTHER failure fell
              // through to "Every visit you have delivered has a note."
              <LoadFailure
                error={notes.error}
                message="We couldn’t load your outstanding notes just now. This is not the same as having none."
                onRetry={() => void notes.refetch()}
              />
            ) : owed.length === 0 ? (
              <SuccessEmpty
                heading="Nothing outstanding"
                message="Every visit you have delivered has a note."
              />
            ) : (
              <ul className="tc-rows">
                {owed.map((n) => (
                  <li key={n.serviceEventId} className="tc-row">
                    <div className="tc-day">
                      <div className="tc-day-head">
                        <span className="tc-row-name">
                          {n.clientInitials} · {dayLabel(n.occurredAt)}
                        </span>
                        {n.isLate ? (
                          <Pill variant="urgent" dot>
                            Late
                          </Pill>
                        ) : null}
                      </div>
                      <p className="rn-muted">{noteState(n)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {/* Writing happens in the mobile app; saying so beats a button that
              does nothing. */}
            <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
              Notes are written in the Phibian app on your phone. This list is what you owe.
            </p>
          </section>
        ) : null}

        {tab === 'profile' ? (
          <section className="rn-card">
            <h2 className="rn-card-title">Your record</h2>
            <dl className="cmp-facts">
              <div>
                <dt>Name</dt>
                <dd>{w.displayName}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{w.roleTitle ?? 'Not recorded'}</dd>
              </div>
              <div>
                <dt>Employment</dt>
                <dd>{w.employmentStatus}</dd>
              </div>
              <div>
                <dt>Expected weekly hours</dt>
                <dd>{w.expectedWeeklyHours != null ? `${w.expectedWeeklyHours}h` : 'Not set'}</dd>
              </div>
              <div>
                <dt>Started</dt>
                <dd>{w.hiredOn ? dayLabel(w.hiredOn) : 'Not recorded'}</dd>
              </div>
            </dl>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** How an outstanding note reads. The state is the server's. */
function noteState(n: documentation.ToWriteItem): string {
  if (n.isLate && n.lagDays != null) return `Outstanding for ${n.lagDays} days`;
  if (n.state === 'draft') return 'Started, not finished';
  return 'Not started';
}
